import { supabase } from './supabase'
import type { MilestoneSetting } from './types'

/**
 * The reads/writes side of the Triggers tab's on/off switch
 * (claude/milestone-triggers-tab-flagged-2026-08-23.md). Same shape as
 * `lib/tasks.ts`: imports `supabase` directly, which puts it out of reach of
 * the plain-Node test harness the same way `tasks.ts`/`sender.ts` already
 * are, so it stays a thin wrapper and the wiring around it is pinned as
 * source text instead (`test-triggers-tab-wiring.mjs`).
 *
 * Seven rows, seeded by the migration — `dbAll`-style paging is not needed
 * for a table that can never grow past that, same reasoning the
 * `sync-metabase.ts` read of this table gives for using a single request.
 */

export async function loadMilestoneSettings(): Promise<{
  settings: MilestoneSetting[] | null
  error: string | null
}> {
  const { data, error } = await supabase.from('milestone_settings').select('*').order('trigger')
  if (error) return { settings: null, error: error.message }
  return { settings: data as MilestoneSetting[], error: null }
}

/**
 * Flips one trigger. `updated_at`/`updated_by` are set in the same write as
 * `enabled` — never a separate call — for the same reason
 * `toggleTaskDone` keeps `done`/`done_at` together: there is no partway a
 * failure can leave them in.
 */
export async function setTriggerEnabled(
  trigger: string,
  enabled: boolean,
  updatedBy: string | null,
): Promise<{ setting: MilestoneSetting | null; error: string | null }> {
  const { data, error } = await supabase
    .from('milestone_settings')
    .update({ enabled, updated_at: new Date().toISOString(), updated_by: updatedBy })
    .eq('trigger', trigger)
    .select('*')
    .single()
  if (error) return { setting: null, error: error.message }
  return { setting: data as MilestoneSetting, error: null }
}
